import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Form, Button, Spin, message, Typography, Checkbox, Alert, Space } from 'antd';
import medicationChecklistItems from '../utils/medicationChecklistItems'; // Import the new checklist

const { Title } = Typography;

const StructuredMedicationChecklistForm = ({ patientId, onSaveSuccess, onCancel }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    const fetchChecklist = useCallback(async () => {
        if (!patientId) {
            console.warn("StructuredMedicationChecklistForm: patientId is missing.");
            setFetchError("Cannot load checklist: Patient ID is missing.");
            return;
        }
        setFetching(true);
        setFetchError(null);
        console.log(`StructuredMedicationChecklistForm: Fetching checklist for patient ID: ${patientId}`);
        try {
            const response = await axios.get(`/patients/${patientId}`);
            const patientData = response?.data?.data?.patient;
            if (!patientData) {
                console.error("Error fetching medication checklist: Invalid data structure received", response.data);
                throw new Error("Received invalid patient data structure from server.");
            }

            // Assuming the checklist data is stored in patient.structuredMedicationChecklist.items
            const currentChecklist = patientData.structuredMedicationChecklist || { items: [] }; 
            console.log("StructuredMedicationChecklistForm: Fetched checklist:", currentChecklist);

            form.setFieldsValue({
                items: currentChecklist.items || [], // Field name is 'items'
            });

        } catch (error) {
            let detailedError = "Failed to load existing medication checklist. Please try again.";
            console.error("StructuredMedicationChecklistForm: Error fetching checklist:", error);
            if (error.response) {
                console.error("Error Response Data:", error.response.data);
                console.error("Error Response Status:", error.response.status);
                detailedError = `Server Error ${error.response.status}: ${error.response.data?.message || 'Could not load checklist'}.`;
            } else if (error.request) {
                console.error("Error Request:", error.request);
                detailedError = "No response from server while fetching checklist. Check network or backend status.";
            } else {
                console.error('Error Message:', error.message);
                detailedError = `Error: ${error.message || 'An unknown error occurred'}`;
            }
            setFetchError(detailedError); 
            message.error("Failed to load medication checklist."); 
        } finally {
            setFetching(false);
        }
    }, [patientId, form]);

    useEffect(() => {
        fetchChecklist();
    }, [fetchChecklist]); 

    const onFinish = async (values) => {
        setLoading(true);
        console.log(`StructuredMedicationChecklistForm: Saving checklist for patient ID: ${patientId}`);
        try {
            let finalItems = values.items || [];
            // Removed filtering logic - all selected options should be saved and displayed
            // User should be able to select "Not taking any medications" along with other options

            const payload = {
                // Ensure the payload structure matches the backend expectation
                structuredMedicationChecklist: {
                    items: finalItems,
                },
            };
            console.log("StructuredMedicationChecklistForm: Saving payload:", payload);

            await axios.patch(`/patients/${patientId}`, payload);

            message.success('Medication checklist updated successfully!');
            if (onSaveSuccess) {
                onSaveSuccess();
            }
        } catch (error) {
            console.error("StructuredMedicationChecklistForm: Error saving checklist:", error);
             let detailedError = "Failed to update medication checklist. Please try again.";
             if (error.response) {
                console.error("Error Response Data:", error.response.data);
                console.error("Error Response Status:", error.response.status);
                detailedError = `Server Error ${error.response.status}: ${error.response.data?.message || 'Could not save checklist'}.`;
            } else if (error.request) {
                console.error("Error Request:", error.request);
                detailedError = "No response from server while saving checklist. Check network or backend status.";
            } else {
                console.error('Error Message:', error.message);
                detailedError = `Error: ${error.message || 'An unknown error occurred'}`;
            }
            message.error(detailedError);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Spin spinning={fetching || loading}>
            {fetchError && <Alert message={fetchError} type="error" showIcon closable style={{ marginBottom: 16 }} />}
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{ items: [] }} // Field name is 'items'
            >
                <Form.Item
                    name="items" // Field name is 'items'
                    label={<Title level={5} style={{marginBottom: '10px'}}>Select applicable medication categories:</Title>} 
                >
                    <Checkbox.Group style={{ width: '100%' }}>
                        <Space direction="vertical"> 
                            {medicationChecklistItems.map(item => (
                                <Checkbox key={item} value={item}>
                                    {item}
                                </Checkbox>
                            ))}
                        </Space>
                    </Checkbox.Group>
                </Form.Item>

                <Form.Item style={{ textAlign: 'right', marginTop: 24, marginBottom: 0 }}> 
                    {onCancel && <Button onClick={onCancel} style={{ marginRight: 8 }}>Cancel</Button>}
                    <Button type="primary" htmlType="submit" loading={loading}>
                        Update Checklist
                    </Button>
                </Form.Item>
            </Form>
        </Spin>
    );
};

export default StructuredMedicationChecklistForm;
